
import { useState } from 'react';
import { parseCSV } from '@/utils/csvParser';
import { RedirectRule } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { Upload } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { normalizePath } from '@/utils/redirectUtils';

interface RedirectUploaderProps {
  onRulesUpdate?: (rules: RedirectRule[]) => void;
}

export const RedirectUploader = ({ onRulesUpdate }: RedirectUploaderProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const { toast } = useToast();
  
  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setIsUploading(true);
    
    try {
      // Read the file contents
      const content = await readFileAsText(file);
      
      // Parse the CSV content
      const parsedData = parseCSV(content, ['from', 'to']);
      
      // Convert and validate the parsed data to RedirectRule type
      const validRules = parsedData
        .filter(item => 
          item.from && 
          item.to && 
          item.from.startsWith('/') && 
          item.to.startsWith('/') &&
          item.from !== item.to // Prevent circular redirects
        )
        .map(item => ({
          from: normalizePath(item.from),
          to: normalizePath(item.to),
          active: true
        })) as RedirectRule[];
      
      if (validRules.length === 0) {
        toast({
          title: 'No valid redirects found',
          description: 'Please check your CSV file format and try again.',
          variant: 'destructive',
        });
        return;
      }
      
      // Save to database
      const { error } = await supabase
        .from('redirect_rules')
        .upsert(
          validRules.map(rule => ({
            from_path: rule.from,
            to_path: rule.to,
            active: rule.active,
            notes: rule.notes
          })),
          {
            onConflict: 'from_path',
            ignoreDuplicates: false
          }
        );
      
      if (error) {
        throw new Error(`Failed to save redirects to database: ${error.message}`);
      }
      
      // Notify parent component about the new rules
      if (onRulesUpdate) {
        onRulesUpdate(validRules);
      }
      
      // Show success toast
      toast({
        title: 'Redirects uploaded successfully',
        description: `${validRules.length} redirect rules have been imported and saved.`,
        variant: 'default',
      });
      
    } catch (error) {
      console.error('Error uploading redirects:', error);
      toast({
        title: 'Error uploading redirects',
        description: 'There was a problem processing your CSV file or saving redirects. Please try again.',
        variant: 'destructive',
      });
    } finally {
      setIsUploading(false);
      // Reset the input file
      event.target.value = '';
    }
  };
  
  // Function to read file as text
  const readFileAsText = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (event) => resolve(event.target?.result as string);
      reader.onerror = (error) => reject(error);
      reader.readAsText(file);
    });
  };
  
  return (
    <div className="space-y-4 p-4 border rounded-lg bg-white">
      <h3 className="text-lg font-medium">Bulk Upload Redirects</h3>
      
      <p className="text-sm text-muted-foreground">
        Upload a CSV file with redirect rules. The CSV must have two columns: "from" and "to".
        Each value should start with a slash (e.g., /old-path,/new-path).
      </p>
      
      <div className="flex items-center gap-2">
        <Input
          type="file"
          accept=".csv"
          onChange={handleFileUpload}
          disabled={isUploading}
          className="flex-1 hidden"
          id="csv-file-input"
        />
        <Button
          type="button"
          variant="outline"
          disabled={isUploading}
          onClick={() => document.getElementById('csv-file-input')?.click()}
          className="w-full flex items-center justify-center gap-2"
        >
          <Upload size={16} />
          {isUploading ? 'Uploading...' : 'Upload CSV File'}
        </Button>
      </div>
      
      <div className="text-xs text-muted-foreground mt-2">
        <p>CSV Format Example:</p>
        <pre className="p-2 bg-muted rounded text-xs">
          from,to
          /blog,/resources/blog
          /services,/services/managed-services
          /contact,/get-started
        </pre>
      </div>
    </div>
  );
};
